// ============================================================================
// Hooks TanStack Query — MON EQUIPE (L10) : roster + sante + rappels
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). La logique d'autorisation vit au
// SERVEUR (M-150 director|admin, M-151 portee) — le SPA consomme viewer.* pour les
// affordances, jamais pour decider (lecon GA-05).
// Les objectifs d'equipe reutilisent les hooks E1b (useCreateObjective/
// useUpdateObjective/useDeleteObjective de useTeams) : meme endpoint, meme garde
// M-204/M-156 — on ne duplique pas une source d'ecriture.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError } from '@gs/api-client';
import type { GestTeamRole } from './useTeams';

// ─── Types (miroir des DTO serveur) ──────────────────────────────────────────

export interface MonEquipeTeam {
  id: string;
  name: string;
  department: string | null;
  directorEmail: string | null;
  directorName: string | null;
}

/**
 * Membre du roster (F-162). M-153 : AUCUN champ de remuneration. M-154 : scores de
 * pulse seulement — le `comment` n'existe pas dans ce type, par construction (le
 * serveur ne le selectionne meme pas).
 */
export interface RosterMember {
  email: string;
  fullName: string | null;
  jobTitle: string | null;
  isDirector: boolean;
  submitted: boolean;
  workloadScore: number | null;
  energyScore: number | null;
  pulsePeriod: string | null;
}

/** M-153 : la seule forme sous laquelle la remuneration atteint le SPA. */
export interface TeamPayrollBase {
  total: number;
  unpricedCount: number;
  year: number;
}

export interface Reminder {
  id: string;
  directorEmail: string;
  title: string;
  body: string | null;
  dueDate: string | null;
  relatedEmail: string | null;
  done: boolean;
  createdAt: string;
}

export interface MonEquipeObjective {
  id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'done';
  targetDate: string | null;
}

export interface MonEquipeResponse {
  viewer: { role: GestTeamRole; email: string; canWriteObjectives?: boolean };
  teams: MonEquipeTeam[];
  selectedTeamId: string | null;
  period: string;
  year?: number;
  roster: RosterMember[];
  payroll: TeamPayrollBase | null;
  objectives: MonEquipeObjective[];
  reminders: Reminder[];
}

/** Pulse individuel COMPLET — page sante seulement (deja director|admin scopee, M-011). */
export interface MemberPulse {
  id: string;
  email: string;
  period: string;
  workloadScore: number;
  energyScore: number;
  comment: string | null;
  submittedAt: string;
}

export interface CheckinAnswer {
  key: string;
  label: string;
  rating: number;
}

export interface Checkin {
  id: string;
  email: string;
  year: number;
  quarter: number;
  answers: CheckinAnswer[];
  note: string | null;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface SanteResponse {
  viewer: { role: GestTeamRole; email: string };
  teams: MonEquipeTeam[];
  selectedTeamId: string | null;
  roster: RosterMember[];
  pulses: MemberPulse[];
  checkins: Checkin[];
}

export interface ReminderCreatePayload {
  title: string;
  body: string | null;
  dueDate: string | null;
}

const ROOT_KEY = ['gest', 'mon-equipe'] as const;
const rosterKey = (team: string | null) => ['gest', 'mon-equipe', 'roster', team ?? ''] as const;
const santeKey = (team: string | null) => ['gest', 'mon-equipe', 'sante', team ?? ''] as const;
const ACCESS_KEY = ['gest', 'mon-equipe', 'access'] as const;

/** Probe capability-aware (M-150) : 403 manager/employee -> canView false, onglet masque. */
export function useMonEquipeAccess(enabled: boolean) {
  return useQuery({
    queryKey: ACCESS_KEY,
    queryFn: async (): Promise<{ canView: boolean }> => {
      try {
        await api.get<MonEquipeResponse>('/api/mon-equipe');
        return { canView: true };
      } catch (err) {
        if (err instanceof ApiForbiddenError) return { canView: false };
        throw err;
      }
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/** F-150..F-166 : bundle de la page (1 requete). */
export function useMonEquipe(teamId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: rosterKey(teamId),
    queryFn: () => api.get<MonEquipeResponse>(`/api/mon-equipe${teamId ? `?team=${encodeURIComponent(teamId)}` : ''}`),
    enabled,
    // 403 (M-150) = etat stable — pas de retry.
    retry: false,
  });
}

/** F-168..F-170 : bundle de la page sante (1 requete). */
export function useSanteEquipe(teamId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: santeKey(teamId),
    queryFn: () => api.get<SanteResponse>(`/api/mon-equipe/sante${teamId ? `?team=${encodeURIComponent(teamId)}` : ''}`),
    enabled,
    retry: false,
  });
}

function useInvalidateMonEquipe() {
  const qc = useQueryClient();
  return () => { void qc.invalidateQueries({ queryKey: ROOT_KEY }); };
}

/**
 * F-166/F-173 : creation d'un rappel. Cree TOUJOURS au nom du viewer — le serveur
 * n'accepte aucun director_email entrant (M-155).
 * NT-L10-01 : `relatedEmail` est accepte par le schema serveur mais le wizard ne
 * l'expose pas — parite PoC reproduite, donc absent de ce payload.
 */
export function useCreateReminder() {
  const invalidate = useInvalidateMonEquipe();
  return useMutation({
    mutationFn: (payload: ReminderCreatePayload) =>
      api.post<{ id: string }>('/api/mon-equipe/reminders', payload),
    onSuccess: () => invalidate(),
  });
}

/** F-166/F-173 : bascule done (ownership M-155 re-verifiee serveur). */
export function useToggleReminder() {
  const invalidate = useInvalidateMonEquipe();
  return useMutation({
    mutationFn: (input: { id: string; done: boolean }) =>
      api.put<{ ok: boolean }>(`/api/mon-equipe/reminders/${input.id}`, { done: input.done }),
    onSuccess: () => invalidate(),
  });
}

/** F-166/F-173 : soft-delete + audit director_reminder.deleted (M-155). */
export function useDeleteReminder() {
  const invalidate = useInvalidateMonEquipe();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/mon-equipe/reminders/${id}`),
    onSuccess: () => invalidate(),
  });
}
